import { IFileType } from "./types";

export const getContentTypeFromIFileType = (t: IFileType) => {
  switch(t) {
    case IFileType.UARPackage:
      return 'application/zip'
    default:
      return t.toString()
  }
}

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * @description Format bytes into a human-readable format
 * @param bytes The number of bytes
 * @param decimals The number of decimal places to include
 * @returns A string representing the bytes in a human-readable format
 */
export function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${sizes[i]}`;
}

/**
 * @description Does a rudimentary parse of the ZIP’s local file headers.
 * It checks that:
 *   • The file begins with a valid ZIP header signature.
 *   • Each entry’s filename does not include any “../” (or other absolute paths).
 *   • The sum of the uncompressed sizes is below a preset limit.
 *   • The number of entries is below a preset limit.
 * @param buffer The ArrayBuffer containing the ZIP file
 * @param maxFileSize The maximum allowed file size in bytes
 * @returns true if the ZIP file is valid, false otherwise
 */
export function validateZipBuffer(buffer: ArrayBuffer, maxFileSize: number) {
  const dv = new DataView(buffer);
  const fileLength = buffer.byteLength;
  let offset = 0;
  let entryCount = 0;
  let totalUncompressedSize = 0;

  // These limits can be adjusted as appropriate for your application:
  const MAX_TOTAL_UNCOMPRESSED_SIZE = maxFileSize * 2; // Double the allowed compressed file size
  const MAX_ENTRIES = 10000; // maximum number of entries allowed

  // A valid ZIP must be at least 30 bytes (the size of a local header)
  if (fileLength < 30) return false;

  // Process each local file header sequentially.
  while (offset < fileLength) {
    // There must be at least 4 bytes for the signature.
    if (offset + 4 > fileLength) break;

    // Local file header signature is 0x50 0x4B 0x03 0x04.
    // DataView reading in little-endian returns 0x04034b50.
    const signature = dv.getUint32(offset, true);
    if (signature !== 0x04034b50) {
      // We’ve reached a block that isn’t a local header (possibly the central directory).
      break;
    }

    // Ensure we have enough bytes for the fixed-size header (30 bytes)
    if (offset + 30 > fileLength) return false;

    // File name length is a 2-byte value at offset +26;
    // extra field length is at offset +28.
    const fileNameLength = dv.getUint16(offset + 26, true);
    const extraFieldLength = dv.getUint16(offset + 28, true);

    // Make sure the file name exists inside the buffer.
    if (offset + 30 + fileNameLength > fileLength) return false;

    // Read and decode the filename.
    const fileNameBytes = new Uint8Array(buffer, offset + 30, fileNameLength);
    const fileName = new TextDecoder("utf-8").decode(fileNameBytes);

    // SECURITY CHECK: reject filenames that try to traverse out of the expected directory.
    // This simple check fails if the filename contains ".." or starts with "/" or a drive letter.
    if (fileName.indexOf("..") !== -1 ||
      fileName.startsWith("/") ||
      /^[a-zA-Z]:[\\/]/.test(fileName)) {
      console.error("Rejected unsafe filename:", fileName);
      return false;
    }

    // Read the compressed and uncompressed sizes.
    const compressedSize = dv.getUint32(offset + 18, true);
    const uncompressedSize = dv.getUint32(offset + 22, true);

    totalUncompressedSize += uncompressedSize;
    if (totalUncompressedSize > MAX_TOTAL_UNCOMPRESSED_SIZE) {
      console.error(`Uncompressed size ${formatBytes(totalUncompressedSize)} exceeds limit ${formatBytes(MAX_TOTAL_UNCOMPRESSED_SIZE)}`);
      return false;
    }

    entryCount++;
    if (entryCount > MAX_ENTRIES) {
      console.error(`Number of entries ${entryCount} exceeds limit ${MAX_ENTRIES}`);
      return false;
    }

    // Calculate the size of this header.
    const headerSize = 30 + fileNameLength + extraFieldLength;

    // Move offset to the beginning of the file data.
    offset += headerSize;

    // Skip the file data.
    offset += compressedSize;

    // (Optional) If bit 3 (0x08) is set in the general purpose flag,
    // there is a data descriptor following the file data.
    const generalPurposeFlag = dv.getUint16(offset - (headerSize + compressedSize) + 6, true);
    if (generalPurposeFlag & 0x08) {
      // Skip an additional 12 bytes for the data descriptor.
      offset += 12;
    }
  }

  return true;
}

export async function validateImage(image: File | Blob, maxFileSize: number = 20000000) { // 20MB
  if (image.size > maxFileSize) return false
  if (image.type !== 'image/jpeg' && image.type !== 'image/png' && image.type !== 'image/gif') {
    console.error('Unsupported image format:', image.type)
    return false
  }

  // Get enough bytes to check all formats
  // PNG needs 8 bytes, GIF needs 6, JPEG needs 2
  const headerBlob = image.slice(0, 8);
  const buffer = await headerBlob.arrayBuffer();
  const header = new Uint8Array(buffer);

  // Check for JPEG Start Of Image marker (FF D8)
  if (image.type === 'image/jpeg' && header[0] === 0xFF && header[1] === 0xD8) {
    return true
  }
  // Check for PNG signature (89 50 4E 47 0D 0A 1A 0A)
  if (image.type === 'image/png' &&
    header[0] === 0x89 && // PNG signature byte
    header[1] === 0x50 && // P
    header[2] === 0x4E && // N
    header[3] === 0x47 && // G
    header[4] === 0x0D && // CR
    header[5] === 0x0A && // LF
    header[6] === 0x1A && // EOF
    header[7] === 0x0A) { // LF
      return true
  }
  // GIF starts with GIF87a or GIF89a
  if (image.type === 'image/gif' &&
    header[0] === 0x47 && // G
    header[1] === 0x49 && // I
    header[2] === 0x46 && // F
    header[3] === 0x38 && // 8
    (header[4] === 0x37 || header[4] === 0x39) && // 7 or 9
    header[5] === 0x61) { // a
      return true
  }

  console.error('Invalid image:', image.type, image.size)
  return false
}
